export type OrderStage = "todo" | "active" | "closed" | "failed"
export type IconName = string

export type ServicePipelinePresentationTone = "warning" | "success" | "danger" | "neutral" | "info"
export type ServicePipelinePresentationIconPosition = "start" | "end"
export type ServicePipelinePresentationBadgeTone = "warning" | "success" | "danger" | "neutral" | "info"
export type ServicePipelineProgressBehavior = "counted" | "terminal"
export type ServicePipelineLifecycleState = "completed" | "canceled"
export type ServicePipelineActionKey =
    | "open_service_drawer"
    | "open_order_details"
    | "open_schedule_input"
    | "open_download"
    | "open_unit_gallery"
    | "show_email_archive_info"
    | string

export interface ServicePipelineStepPresentation {
    icon?: IconName
    iconPosition?: ServicePipelinePresentationIconPosition
    tone?: ServicePipelinePresentationTone
    showStatusLabel?: boolean
    fillBackground?: boolean
    dashedIcon?: boolean
    accentTitle?: boolean
    highlightBorder?: boolean
    badge?: string
    badgeTone?: ServicePipelinePresentationBadgeTone
    showActionRequired?: boolean
    actionKey?: ServicePipelineActionKey
    colorOverride?: `#${string}` | `var(--${string})`
}

export interface ServicePipelineStage<TServiceStatus extends string = string> {
    id: string
    serviceStatus: TServiceStatus
    stage: OrderStage
    title: string
    description: string
    presentation?: ServicePipelineStepPresentation
    progressBehavior?: ServicePipelineProgressBehavior
    lifecycleState?: ServicePipelineLifecycleState
}

export type ServicePipeline<TServiceStatus extends string = string> = ReadonlyArray<ServicePipelineStage<TServiceStatus>>

let liveServicePipelines: Record<string, ServicePipeline> | null = null

export const setLiveServicePipelines = (value: unknown): boolean => {
    if (value == null || typeof value !== 'object' || Array.isArray(value)) {
        liveServicePipelines = null
        return false
    }
    const entries = Object.entries(value as Record<string, unknown>)
    const valid = entries.every(([serviceId, pipeline]) => serviceId.trim() !== '' && Array.isArray(pipeline) && pipeline.every((stage: any) => typeof stage?.id === 'string' && typeof stage?.serviceStatus === 'string' && typeof stage?.title === 'string' && typeof stage?.description === 'string'))
    liveServicePipelines = valid ? Object.fromEntries(entries.map(([serviceId, pipeline]) => [serviceId, withPipelineLifecycleMetadata(pipeline as ServicePipeline)])) : null
    return valid
}

export const hasLiveServicePipelines = (): boolean => liveServicePipelines != null

export const normalizeServiceStatusToken = (value: unknown): string => {
    if (typeof value !== "string") return ""
    return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, " ").trim()
}

export const isTerminalServiceStatus = (status: unknown): boolean => {
    const normalized = normalizeServiceStatusToken(status)
    return normalized === normalizeServiceStatusToken("Completed")
        || normalized === normalizeServiceStatusToken("Canceled")
}

const resolveLifecycleStateFromStatus = (status: unknown): ServicePipelineLifecycleState | undefined => {
    const normalized = normalizeServiceStatusToken(status)
    if (normalized === normalizeServiceStatusToken("Completed")) return "completed"
    if (normalized === normalizeServiceStatusToken("Canceled")) return "canceled"
    return undefined
}

export const isTerminalPipelineStage = (stage: ServicePipelineStage | undefined | null): boolean => {
    if (stage == null) return false
    if (stage.progressBehavior === "terminal") return true
    return stage.lifecycleState != null || isTerminalServiceStatus(stage.serviceStatus)
}

export const getCountedPipelineStages = (pipeline: readonly ServicePipelineStage[]): ServicePipelineStage[] => {
    return pipeline.filter((stage) => !isTerminalPipelineStage(stage))
}

export const resolvePipelineProgressPosition = ({
    pipeline,
    currentStepId,
    statusText,
}: {
    pipeline: readonly ServicePipelineStage[]
    currentStepId?: string
    statusText?: string
}): {
    totalSteps: number
    currentStep: number
    matchedStage?: ServicePipelineStage
    matchedTerminalStage?: ServicePipelineStage
} => {
    if (!Array.isArray(pipeline) || pipeline.length === 0) {
        return { totalSteps: 1, currentStep: 1 }
    }

    const normalizedStatus = normalizeServiceStatusToken(statusText)
    const matchedStageById = typeof currentStepId === "string" && currentStepId.trim() !== ""
        ? pipeline.find((item) => item.id === currentStepId)
        : undefined
    const matchedStageByStatus = matchedStageById == null && normalizedStatus !== ""
        ? pipeline.find((item) => normalizeServiceStatusToken(item.serviceStatus) === normalizedStatus)
        : undefined
    const matchedStage = matchedStageById ?? matchedStageByStatus

    const countedStages = getCountedPipelineStages(pipeline)
    const safeTotal = Math.max(1, countedStages.length)

    if (matchedStage != null && isTerminalPipelineStage(matchedStage)) {
        return {
            totalSteps: safeTotal,
            currentStep: safeTotal,
            matchedStage,
            matchedTerminalStage: matchedStage,
        }
    }

    if (matchedStage == null && isTerminalServiceStatus(statusText)) {
        return {
            totalSteps: safeTotal,
            currentStep: safeTotal,
        }
    }

    const resolvedIndex = matchedStage != null
        ? countedStages.findIndex((item) => item.id === matchedStage.id)
        : -1
    const currentStep = resolvedIndex >= 0 ? (resolvedIndex + 1) : 1

    return {
        totalSteps: safeTotal,
        currentStep: Math.max(1, Math.min(currentStep, safeTotal)),
        matchedStage,
    }
}

const withPipelineLifecycleMetadata = <TPipeline extends ServicePipeline>(pipeline: TPipeline): ServicePipeline => {
    return pipeline.map((stage) => {
        const lifecycleState = stage.lifecycleState ?? resolveLifecycleStateFromStatus(stage.serviceStatus)
        return {
            ...stage,
            lifecycleState,
            progressBehavior: stage.progressBehavior ?? (lifecycleState != null ? "terminal" : "counted"),
        }
    })
}

const RAW_SERVICE_PIPELINES = {


    find_tenant: [
        {
            id: 'noc_initiated',
            serviceStatus: 'NOC Initiated',
            stage: 'todo',
            title: 'Tenant Sourcing In Progress',
            description: 'Tenant sourcing process has been initiated. We are preparing the authorization required to activate listing, marketing, and broker distribution.',
            presentation: {
                tone: 'warning',
                icon: 'schedule', }
        },
        {
            id: 'sent_for_signature',
            serviceStatus: 'Sent for Signature',
            stage: 'active',
            title: 'Signature Required',
            description: 'The authorization document has been sent and must be signed to activate tenant sourcing, listing, and broker access.',
            presentation: {
                tone: 'danger',
                icon: 'stylus_note',
                showActionRequired: true,
                actionKey: 'open_sign_document'
            }
        },
        {
            id: 'signed',
            serviceStatus: 'Signed',
            stage: 'closed',
            title: 'Preparing Activation',
            description: 'Authorization is signed. Our team is now preparing your unit for active tenant sourcing exposure.',
            presentation: {
                tone: 'success',
                icon: 'check_circle', }
        },
        {
            id: 'listed_for_brokers',
            serviceStatus: 'Listed For Brokers',
            stage: 'active',
            title: 'Tenant Sourcing Active',
            description: 'Tenant sourcing is active; brokers can market the property, arrange viewings, and submit offers.',
            presentation: {
                tone: 'success',
                icon: 'leaderboard', }
        },
        {
            id: 'completed',
            serviceStatus: 'Completed',
            stage: 'closed',
            title: 'Completed',
            description: 'Tenant sourcing request is completed.',
            presentation: {
                tone: 'success',
                icon: 'check_circle', }
        },
        {
            id: 'canceled',
            serviceStatus: 'Canceled',
            stage: 'failed',
            title: 'Request Canceled',
            description: 'Tenant sourcing request was canceled.',
            presentation: {
                tone: 'danger',
                icon: 'close_small'
            }
        },
        // {
        //     id: 'completed',
        //     serviceStatus: 'Completed',
        //     stage: 'closed',
        //     title: 'Tenant Sourcing Active',
        //     description: 'Tenant sourcing is active. Agents can advertise your property, schedule viewings, and submit offers.',
        //     presentation: {
        //         tone: 'success',
        //         icon: 'check_circle',
        //         actionKey: 'open_service_drawer'
        //     }
        // },
        // {
        //     id: 'request_received',
        //     serviceStatus: 'In-process',
        //     stage: 'todo',
        //     title: 'Request Received',
        //     description: 'Your request has been received. Our team will verify your unit details and prepare your listing for the broker network.',
        //     presentation: {
        //         tone: 'warning',
        //         icon: 'schedule',
        //         actionKey: 'open_service_drawer'
        //     }
        // },
        // {
        //     id: 'payment_successful',
        //     serviceStatus: 'Paid',
        //     stage: 'todo',
        //     title: 'Pending Verification',
        //     description: 'Your order is confirmed and awaiting verification before we publish it to our brokers.',
        //     presentation: {
        //         tone: 'success',
        //         icon: 'paid',
        //         actionKey: 'open_service_drawer'
        //     }
        // },
        // {
        //     id: 'listed_for_brokers',
        //     serviceStatus: 'Listed For Brokers',
        //     stage: 'active',
        //     title: 'Listed for Brokers',
        //     description: 'Your property is now live on the BSO Broker Network, accessible to 3,000+ certified agents.',
        //     presentation: {
        //         tone: 'warning',
        //         icon: 'leaderboard',
        //         actionKey: 'open_service_drawer'
        //     }
        // },
        // {
        //     id: 'advertisements_active',
        //     serviceStatus: 'Advertisements Active',
        //     stage: 'active',
        //     title: 'Advertisements Active',
        //     description: 'Brokers have started active marketing; your property is now being promoted across premium portals.',
        //     presentation: {
        //         tone: 'warning',
        //         icon: 'leaderboard',
        //         actionKey: 'open_service_drawer'
        //     }
        // },
        // {
        //     id: 'viewing_requested',
        //     serviceStatus: 'Viewing Requested',
        //     stage: 'active',
        //     title: 'Viewing Requested',
        //     description: 'A broker has requested to show your property to a potential lead.',
        //     presentation: {
        //         tone: 'warning',
        //         icon: 'schedule',
        //         badge: 'SCHEDULE',
        //         badgeTone: 'warning',
        //         showActionRequired: true,
        //         actionKey: 'open_order_details'
        //     }
        // },
        // {
        //     id: 'viewing_scheduled',
        //     serviceStatus: 'Viewing Scheduled',
        //     stage: 'active',
        //     title: 'Viewing Scheduled',
        //     description: 'Access has been coordinated; the property will be shown at the scheduled time.',
        //     presentation: {
        //         tone: 'warning',
        //         icon: 'schedule',
        //         actionKey: 'open_service_drawer'
        //     }
        // },
        // {
        //     id: 'feedback_received',
        //     serviceStatus: 'Feedback Received',
        //     stage: 'active',
        //     title: 'Feedback Received',
        //     description: "The viewing is complete; you can now review the broker's notes and lead interest.",
        //     presentation: {
        //         tone: 'warning',
        //         icon: 'description',
        //         actionKey: 'open_service_drawer'
        //     }
        // },
        // {
        //     id: 'offer_received',
        //     serviceStatus: 'Offer Received',
        //     stage: 'active',
        //     title: 'Offer Received',
        //     description: 'A formal proposal has been submitted for your review and action.',
        //     presentation: {
        //         tone: 'warning',
        //         icon: 'description',
        //         badge: 'REVIEW',
        //         badgeTone: 'warning',
        //         showActionRequired: true,
        //         actionKey: 'open_order_details'
        //     }
        // },
        // {
        //     id: 'negotiating',
        //     serviceStatus: 'Negotiating',
        //     stage: 'active',
        //     title: 'Negotiating',
        //     description: 'This unit is currently in the negotiation stage.',
        //     presentation: {
        //         tone: 'warning',
        //         icon: 'hourglass_bottom',
        //         actionKey: 'open_service_drawer'
        //     }
        // },
        // {
        //     id: 'offer_declined',
        //     serviceStatus: 'Offer Declined',
        //     stage: 'closed',
        //     title: 'Offer Declined',
        //     description: 'You have declined the offer; the property remains active on the market.',
        //     presentation: {
        //         tone: 'neutral',
        //         icon: 'description',
        //         actionKey: 'open_service_drawer'
        //     }
        // },
        // {
        //     id: 'offer_accepted',
        //     serviceStatus: 'Offer Accepted',
        //     stage: 'closed',
        //     title: 'Offer Accepted',
        //     description: 'The offer for this unit has been finalized.',
        //     presentation: {
        //         tone: 'success',
        //         icon: 'check_circle',
        //         actionKey: 'open_service_drawer'
        //     }
        // },
        // {
        //     id: 'request_canceled',
        //     serviceStatus: 'Canceled',
        //     stage: 'failed',
        //     title: 'Request Canceled',
        //     description: 'Your request was canceled. Please check the details in your order history or contact support.',
        //     presentation: {
        //         tone: 'danger',
        //         icon: 'close_small',
        //     }
        // },
    ],

    // lease_execution ? give keys - access cards , escort to apppartment , take small paper for what you   
    //  inventory_checklist ?  
    // google calendar link for scheduled date , buffer 24, depend on their busy  , gap  , schegule date separate 2 parts 
    // scheduled date for inventory , emergancy call if happens on the day of or day before , reschegule option if they miss or cancel withing 24 h of scheduled date , report in mail with details and pdf if ready  , link to report in app if ready , separate for rent and sale
    // final sent report in mail with details and pdf if ready  , link to report in app if ready , separate for rent and sale
    // Price need to check 



    inventory_checklist: [
        {
            id: "request_received",
            serviceStatus: "Request Received",
            stage: "todo",
            title: "Request Received",
            description: "Your inventory checklist request has been received.",
            presentation: { tone: "neutral", icon: "schedule", dashedIcon: true }
        },
        {
            id: "scheduled",
            serviceStatus: "Scheduled",
            stage: "active",
            title: "Checklist Scheduled",
            description: "Inventory checklist visit has been scheduled.",
            presentation: { tone: "warning", icon: "schedule" }
        },
        {
            id: "checklist_in_progress",
            serviceStatus: "Checklist In Progress",
            stage: "active",
            title: "Checklist In Progress",
            description: "Our team is completing your unit inventory checklist.",
            presentation: { tone: "warning", icon: "description" }
        },
        {
            id: "report_ready",
            serviceStatus: "Report Ready",
            stage: "closed",
            title: "Report Ready",
            description: "Your inventory checklist report is ready.",
            presentation: { tone: "success", icon: "check_circle" }
        },
        {
            id: "canceled",
            serviceStatus: "Canceled",
            stage: "failed",
            title: "Request Canceled",
            description: "Your inventory checklist request was canceled.",
            presentation: { tone: "danger", icon: "close_small" }
        },
    ],

    lease_execution: [
        {
            id: "request_received",
            serviceStatus: "Request Received",
            stage: "todo",
            title: "Request Received",
            description: "Your lease execution request has been received.",
            presentation: { tone: "neutral", icon: "schedule", dashedIcon: true }
        },
        {
            id: "in_progress",
            serviceStatus: "In Progress",
            stage: "active",
            title: "In Progress",
            description: "Lease execution coordination is in progress.",
            presentation: { tone: "warning", icon: "description" }
        },
        {
            id: "completed",
            serviceStatus: "Completed",
            stage: "closed",
            title: "Completed",
            description: "Lease execution has been completed.",
            presentation: { tone: "success", icon: "check_circle" }
        },
        {
            id: "canceled",
            serviceStatus: "Canceled",
            stage: "failed",
            title: "Request Canceled",
            description: "Lease execution request was canceled.",
            presentation: { tone: "danger", icon: "close_small" }
        },
    ],

    // google calendar link for scheduled date , buffer 24, depend on their busy  , gap  , schegule date separate 2 parts
    // scheduled date for inspection , emergancy call if happens on the day of or day before , reschegule option if they miss or cancel withing 24 h of scheduled date , report in mail with details and pdf if ready  , link to report in app if ready , separate for rent and sale
    // final sent report in mail with details and pdf if ready  , link to report in app if ready , separate for rent and sale
    // Price need to check
    request_inspection: [
        {
            id: 'request_received',
            serviceStatus: 'Request Received',
            stage: 'todo',
            title: 'Request Received',
            description: 'Your inspection request has been received.',
            presentation: {
                tone: 'neutral',
                icon: 'schedule',
                dashedIcon: true, }
        },
        // {
        //     id: 'request_confirmed',
        //     serviceStatus: 'Request Confirmed',
        //     stage: 'todo',
        //     title: 'Request Confirmed',
        //     description: 'Your inspection request has been confirmed and queued for scheduling.',
        //     presentation: {
        //         tone: 'warning',
        //         icon: 'schedule',
        //         actionKey: 'open_service_drawer',
        //     }
        // },
        {
            id: 'inspection_scheduled',
            serviceStatus: 'Inspection Scheduled',
            stage: 'active',
            title: 'Inspection Scheduled',
            description: 'The inspection has been scheduled and assigned.',
            presentation: {
                tone: 'warning',
                icon: 'schedule',
            }
        },
        {
            id: 'inspection_in_progress',
            serviceStatus: 'Inspection in Progress',
            stage: 'active',
            title: 'Inspection in Progress',
            description: 'The property inspection is currently in progress.',
            presentation: {
                tone: 'warning',
                icon: 'construction',
            }
        },
        {
            id: 'report_ready',
            serviceStatus: 'Report Ready',
            stage: 'closed',
            title: 'Report Ready',
            description: 'Your property inspection report is ready for review.',
            presentation: {
                tone: 'success',
                icon: 'check_circle',
            }
        },
        {
            id: 'request_canceled',
            serviceStatus: 'Canceled',
            stage: 'failed',
            title: 'Request Canceled',
            description: 'Your request was canceled. Please contact support for help.',
            presentation: {
                tone: 'danger',
                icon: 'close_small',
            }
        },
    ],


    // 24 h buffer 
    // reschegule 
    // report in mail
    // if separate 500 or more

    unit_photography: [
        {
            id: 'request_received',
            serviceStatus: 'Request Received',
            stage: 'todo',
            title: 'Request Received',
            description: 'Your Pro Photography request has been received.',
            presentation: {
                tone: 'neutral',
                icon: 'schedule',
                dashedIcon: true, }
        },
        {
            id: 'need_start_date',
            serviceStatus: 'NeedStartDate',
            stage: 'todo',
            title: 'Add Start Date',
            description: 'Your order is confirmed. Please set a preferred date and time to schedule the photography session.',
            presentation: {
                tone: 'warning',
                icon: 'schedule',
                showActionRequired: true,
                actionKey: 'open_schedule_input'
            }
        },
        {
            id: 'scheduled',
            serviceStatus: 'Scheduled',
            stage: 'active',
            title: 'Photography Scheduled',
            description: 'Your photography session is booked. Please ensure the access to the property is ready.',
            presentation: {
                tone: 'warning',
                icon: 'schedule', }
        },
        {
            id: 'shoot_completed',
            serviceStatus: 'ShootCompleted',
            stage: 'active',
            title: 'Shoot Completed',
            description: 'The photography session is complete. Our team is preparing your final media package.',
            presentation: {
                tone: 'warning',
                icon: 'imagesmode', }
        },
        {
            id: 'photos_ready',
            serviceStatus: 'PhotosReady',
            stage: 'closed',
            title: 'Photos Ready',
            description: 'Your professional gallery is ready. You can preview the updated unit photos now. The full archive has also been sent to your email.',
            presentation: {
                tone: 'success',
                icon: 'imagesmode',
                iconPosition: 'end',
                actionKey: 'open_unit_gallery'
            }
        },
        {
            id: 'canceled',
            serviceStatus: 'Canceled',
            stage: 'failed',
            title: 'Request Canceled',
            description: 'Your request was canceled. Please check details in your order history or contact support.',
            presentation: {
                tone: 'danger',
                icon: 'close_small'
            }
        },
    ],


    // report also in pdf if ready 
    // theris link if open 
    // rent sale separate

    property_rent_valuation: [
    
        {
            id: 'request_received',
            serviceStatus: 'Request Received',
            stage: 'todo',
            title: 'Request Received',
            description: 'Your valuation request is confirmed. Our analysts are beginning their research on your property.',
            presentation: {
                tone: 'warning',
                icon: 'schedule',
                iconPosition : "end",
                fillBackground: false,
                dashedIcon: true,
                accentTitle: true,
                highlightBorder: true, }
        },


     
        {
            id: 'market_research',
            serviceStatus: 'Market Research',
            stage: 'active',
            title: 'Market Research',
            description: 'We are analyzing recent transactions and current rental trends for your unit',
            presentation: {
                tone: 'warning',
                icon: 'analytics', }
        },
        {
            id: 'generating_report',
            serviceStatus: 'Generating Report',
            stage: 'active',
            title: 'Generating Report',
            description: 'Our experts are finalizing your data-driven rental valuation.',
            presentation: {
                tone: 'warning',
                icon: 'description', }
        },
        {
            id: 'report_ready',
            serviceStatus: 'Report Ready',
            stage: 'closed',
            title: 'Report Ready',
            description: ' Your Property Rent Valuation is complete! Tap here to view and download your report.',
            presentation: {
                tone: 'success',
                icon: 'download',
                iconPosition: 'end',
                actionKey: 'open_download'
            }
        },
        {
            id: 'request_canceled',
            serviceStatus: 'Canceled',
            stage: 'failed',
            title: 'Request Canceled',
            description: 'Your request was canceled. Please check details in your order history or contact support.',
            presentation: {
                tone: 'danger',
                icon: 'close_small',
            }
        },
    ],


    service_unit_maintenance: [
        {
            id: 'request_received',
            serviceStatus: 'Request Received',
            stage: 'todo',
            title: 'Request Received',
            description: 'Your maintenance coordination request has been received. We have started initial processing.',
            presentation: {
                tone: 'warning',
                icon: 'schedule',
            }
        },
        // {
        //     id: 'comparing_quotes',
        //     serviceStatus: 'Comparing Quotes',
        //     stage: 'active',
        //     title: 'Comparing Quotes',
        //     description: 'We are gathering competitive bids from our vetted vendor network to find you the best value.',
        //     presentation: {
        //         tone: 'warning',
        //         icon: 'leaderboard',
        //         actionKey: 'open_service_drawer',
        //     }
        // },
        // {
        //     id: 'quotes_ready_for_review',
        //     serviceStatus: 'Quotes Ready For Review',
        //     stage: 'active',
        //     title: 'Quotes Ready for Review',
        //     description: 'The top 5 quotes are ready. Please select your preferred option and secure the funds to begin.',
        //     presentation: {
        //         tone: 'warning',
        //         icon: 'paid',
        //         showActionRequired: true,
        //         actionKey: 'open_order_details',
        //     }
        // },
        // {
        //     id: 'payment_received',
        //     serviceStatus: 'Payment Received',
        //     stage: 'active',
        //     title: 'Payment Received',
        //     description: 'Your payment is safely held by BSO. We have authorized the vendor to start the work.',
        //     presentation: {
        //         tone: 'warning',
        //         icon: 'paid',
        //         actionKey: 'open_service_drawer',
        //     }
        // },
        {
            id: 'work_in_progress',
            serviceStatus: 'Work In Progress',
            stage: 'active',
            title: 'Work in Progress',
            description: 'The vendor is currently on-site. BSO is monitoring the progress to ensure standards are met.',
            presentation: {
                tone: 'warning',
                icon: 'construction',
            }
        },
        // {
        //     id: 'awaiting_your_approval',
        //     serviceStatus: 'Awaiting Your Approval',
        //     stage: 'active',
        //     title: 'Awaiting Your Approval',
        //     description: 'Work is complete. Please verify you are happy with the result so we can finalize the project.',
        //     presentation: {
        //         tone: 'warning',
        //         icon: 'check_circle',
        //         showActionRequired: true,
        //         actionKey: 'open_order_details',
        //     }
        // },
        // {
        //     id: 'reworking_for_quality',
        //     serviceStatus: 'Reworking For Quality',
        //     stage: 'active',
        //     title: 'Reworking for Quality',
        //     description: "The work didn't meet our standards. We've instructed the vendor to fix it at no extra cost to you.",
        //     presentation: {
        //         tone: 'warning',
        //         icon: 'build_circle',
        //         actionKey: 'open_service_drawer',
        //     }
        // },
        {
            id: 'maintenance_completed',
            serviceStatus: 'Maintenance Completed',
            stage: 'closed',
            title: 'Maintenance Completed',
            description: 'You are satisfied and the project is closed. Your warranty period is now active.',
            presentation: {
                tone: 'success',
                icon: 'check_circle',
            }
        },
        {
            id: 'request_canceled',
            serviceStatus: 'Canceled',
            stage: 'failed',
            title: 'Request Canceled',
            description: 'Your request was canceled. Please check details in your order history or contact support.',
            presentation: {
                tone: 'danger',
                icon: 'close_small',
            }
        },
    ],
    service_rental_dispute: [
        {
            id: 'request_received',
            serviceStatus: 'Request Received',
            stage: 'todo',
            title: 'Request Received',
            description: 'Your request has been received; a BSO legal specialist is reviewing your case details.',
            presentation: {
                tone: 'warning',
                icon: 'schedule', }
        },
        {
            id: 'case_assessment',
            serviceStatus: 'Case Assessment',
            stage: 'active',
            title: 'We are currently assessing',
            description: 'We are evaluating the legal strength of your position based on RDC regulations.',
            presentation: {
                tone: 'warning',
                icon: 'description', }
        },
        {
            id: 'legal_advice_ready',
            serviceStatus: 'Legal Advice Ready',
            stage: 'active',
            title: 'Legal Advice Ready',
            description: 'Our assessment is complete. Tap here to view our suggested next steps and strategy.',
            presentation: {
                tone: 'warning',
                icon: 'description', }
        },
        // {
        //     id: 'awaiting_payment',
        //     serviceStatus: 'Awaiting Payment',
        //     stage: 'active',
        //     title: 'Awaiting Payment',
        //     description: 'To proceed with filing the formal case/notice, please complete the service payment.',
        //     presentation: {
        //         tone: 'warning',
        //         icon: 'paid',
        //         badge: 'PAYMENT',
        //         badgeTone: 'warning',
        //         showActionRequired: true,
        //         actionKey: 'open_order_details'
        //     }
        // },
        // {
        //     id: 'action_in_progress',
        //     serviceStatus: 'Action In Progress',
        //     stage: 'active',
        //     title: 'Action in Progress',
        //     description: 'Payment received; our team is now filing the necessary legal notices or RDC claims.',
        //     presentation: {
        //         tone: 'warning',
        //         icon: 'hourglass_bottom',
        //         actionKey: 'open_service_drawer'
        //     }
        // },
        // {
        //     id: 'case_update',
        //     serviceStatus: 'Case Update',
        //     stage: 'active',
        //     title: 'Case Update',
        //     description: 'There is a new development or hearing update regarding your dispute. Check the app for details.',
        //     presentation: {
        //         tone: 'warning',
        //         icon: 'description',
        //         actionKey: 'open_service_drawer'
        //     }
        // },
        {
            id: 'dispute_resolved',
            serviceStatus: 'Dispute Resolved',
            stage: 'closed',
            title: 'Dispute Resolved',
            description: 'The matter has been settled or a judgment has been issued. Your case is now closed.',
            presentation: {
                tone: 'success',
                icon: 'check_circle', }
        },
        {
            id: 'request_canceled',
            serviceStatus: 'Canceled',
            stage: 'failed',
            title: 'Request Canceled',
            description: 'Your request was canceled. Please check details in your order history or contact support.',
            presentation: {
                tone: 'danger',
                icon: 'close_small',
            }
        },
    ],

    service_property_management: [
        {
            id: 'request_received',
            serviceStatus: 'Request Received',
            stage: 'todo',
            title: 'Request Received',
            description: 'Your request has been received. A management expert will contact you shortly to discuss your unit.',
            presentation: {
                tone: 'warning',
                icon: 'schedule',
                iconPosition: 'end',
                fillBackground: false,
                dashedIcon: true,
                accentTitle: true,
                highlightBorder: true, }
        },
        // {
        //     id: 'contract_ready',
        //     serviceStatus: 'Contract Ready',
        //     stage: 'active',
        //     title: 'Contract Ready',
        //     description: 'Your management agreement is ready. Please review the terms and levels of coverage in-app.',
        //     presentation: {
        //         tone: 'warning',
        //         icon: 'description',
        //         showActionRequired: true,
        //         actionKey: 'open_order_details',
        //     }
        // },
        {
            id: 'awaiting_signature',
            serviceStatus: 'Awaiting Signature',
            stage: 'active',
            title: 'Awaiting Signature',
            description: 'Please sign the agreement to authorize BSO to manage your property and represent you.',
            presentation: {
                tone: 'warning',
                icon: 'stylus_note',
                showActionRequired: true,
                actionKey: 'open_order_details',
            }
        },
        // {
        //     id: 'awaiting_payment',
        //     serviceStatus: 'Awaiting Payment',
        //     stage: 'active',
        //     title: 'Awaiting Payment',
        //     description: 'Contract signed! Please complete the payment to activate your professional management plan.',
        //     presentation: {
        //         tone: 'warning',
        //         icon: 'paid',
        //         showActionRequired: true,
        //         actionKey: 'open_order_details',
        //     }
        // },
        {
            id: 'onboarding_unit',
            serviceStatus: 'Onboarding Unit',
            stage: 'active',
            title: 'Onboarding Unit',
            description: 'We are now setting up your digital portfolio and notifying relevant parties of our management.',
            presentation: {
                tone: 'warning',
                icon: 'apartment',
            }
        },
        {
            id: 'fully_managed',
            serviceStatus: 'Fully Managed',
            stage: 'closed',
            title: 'Fully Managed',
            description: 'Your unit is now fully managed by BSO. Relax while we handle the rest. Our property manager will get in touch with you.',
            presentation: {
                tone: 'success',
                icon: 'check_circle',
                actionKey: 'open_service_drawer',
            }
        },
        {
            id: 'request_canceled',
            serviceStatus: 'Canceled',
            stage: 'failed',
            title: 'Request Canceled',
            description: 'Your request was canceled. Please check details in your order history or contact support.',
            presentation: {
                tone: 'danger',
                icon: 'close_small',
            }
        },
    ],

  

    tenant_screening: [
        {
            id: 'request_received',
            serviceStatus: 'Request Received',
            stage: 'todo',
            title: 'Request Received',
            description: "Your request has been received; please provide the potential tenant's details.",
            presentation: {
                tone: 'warning',
                icon: 'schedule', }
        },
        {
            id: 'in_progress',
            serviceStatus: 'Checking Records',
            stage: 'active',
            title: 'Checking Records',
            description: "We are currently verifying the tenant's background, including employment status and rental history.",
            presentation: {
                tone: 'warning',
                icon: 'description', }
        },
        {
            id: 'final_review',
            serviceStatus: 'Final Review',
            stage: 'active',
            title: 'Final Review',
            description: 'Our team is performing a final check on the collected data to ensure a high-quality match.',
            presentation: {
                tone: 'warning',
                icon: 'description',
                badge: 'REVIEW',
                badgeTone: 'warning',
                showActionRequired: true,
                actionKey: 'open_order_details'
            }
        },
        {
            id: 'request_canceled',
            serviceStatus: 'Canceled',
            stage: 'failed',
            title: 'Request Canceled',
            description: 'Your request was canceled. Please check details in your order history or contact support.',
            presentation: {
                tone: 'danger',
                icon: 'close_small',
            }
        },
    ],

    service_gifting_property: [
        {
            id: 'request_received',
            serviceStatus: 'In-process',
            stage: 'todo',
            title: 'Request Received',
            description: 'Your request has been received; our specialist will contact you to confirm the relationship and requirements.',
            presentation: {
                tone: 'warning',
                icon: 'schedule', }
        },
        // {
        //     id: 'verifying_documents',
        //     serviceStatus: 'Verifying Documents',
        //     stage: 'active',
        //     title: 'Verifying Documents',
        //     description: 'We are reviewing the Title Deed and proof of relationship (e.g., birth/marriage certificates) for DLD.',
        //     presentation: {
        //         tone: 'warning',
        //         icon: 'description',
        //         actionKey: 'open_service_drawer'
        //     }
        // },
        {
            id: 'dld_coordination',
            serviceStatus: 'DLD Coordination',
            stage: 'active',
            title: 'DLD Coordination',
            description: 'We are coordinating with the Dubai Land Department to schedule the gifting transfer.',
            presentation: {
                tone: 'warning',
                icon: 'description', }
        },
        {
            id: 'transfer_in_progress',
            serviceStatus: 'Transfer In Progress',
            stage: 'active',
            title: 'Transfer in Progress',
            description: 'The gifting process is underway; we are finalizing the official change of ownership.',
            presentation: {
                tone: 'warning',
                icon: 'build_circle', }
        },
        {
            id: 'title_deed_ready',
            serviceStatus: 'Title Deed Ready',
            stage: 'closed',
            title: 'Title Deed Ready',
            description: 'Success! The transfer is complete. Your new Title Deed is ready for download.',
            presentation: {
                tone: 'success',
                icon: 'check_circle', }
        },
        {
            id: 'request_canceled',
            serviceStatus: 'Canceled',
            stage: 'failed',
            title: 'Request Canceled',
            description: 'Your request was canceled. Please check details in your order history or contact support.',
            presentation: {
                tone: 'danger',
                icon: 'close_small'
            }
        },
    ],

  
    noc_generation: [
        {
            id: 'noc_initiated',
            serviceStatus: 'NOC Initiated',
            stage: 'todo',
            title: 'NOC Initiated',
            description: 'Your marketing authorization process has been initiated.',
            presentation: {
                tone: 'warning',
                icon: 'schedule', }
        },
        {
            id: 'sent_for_signature',
            serviceStatus: 'Sent for Signature',
            stage: 'active',
            title: 'Sent for Signature',
            description: 'The authorization document has been sent for digital signature.',
            presentation: {
                tone: 'warning',
                icon: 'stylus_note',
                actionKey: 'open_sign_document'
            }
        },
        // {
        //     id: 'awaiting_signature',
        //     serviceStatus: 'Awaiting Signature',
        //     stage: 'active',
        //     title: 'Awaiting Signature',
        //     description: 'Waiting for required parties to complete digital signature.',
        //     presentation: {
        //         tone: 'warning',
        //         icon: 'stylus_note',
        //         showActionRequired: true,
        //         actionKey: 'open_order_details'
        //     }
        // },
        {
            id: 'signed',
            serviceStatus: 'Signed',
            stage: 'active',
            title: 'Signed',
            description: 'Signature is completed. Final compliance checks are in progress.',
            presentation: {
                tone: 'warning',
                icon: 'check_circle', }
        },
        {
            id: 'completed',
            serviceStatus: 'Completed',
            stage: 'closed',
            title: 'Completed',
            description: 'Marketing authorization is completed and ready for downstream processing.',
            presentation: {
                tone: 'success',
                icon: 'check_circle', }
        },
        {
            id: 'request_canceled',
            serviceStatus: 'Canceled',
            stage: 'failed',
            title: 'Request Canceled',
            description: 'NOC process was canceled. Please contact support for details.',
            presentation: {
                tone: 'danger',
                icon: 'close_small'
            }
        },
        {
            id: 'expired',
            serviceStatus: 'Expired',
            stage: 'failed',
            title: 'Expired',
            description: 'NOC signature request has expired and requires re-initiation.',
            presentation: {
                tone: 'danger',
                icon: 'close_small'
            }
        },
        {
            id: 'failed',
            serviceStatus: 'Failed',
            stage: 'failed',
            title: 'Failed',
            description: 'NOC process failed due to a technical or compliance issue.',
        },
    ],

    service_tenant_renewal_retention: [
        {
            id: 'request_received',
            serviceStatus: 'Request Received',
            stage: 'todo',
            title: 'Request Received',
            description: 'Your renewal support request has been received and assigned to our team.',
            presentation: {
                tone: 'warning',
                icon: 'schedule', }
        },
        {
            id: 'in_process',
            serviceStatus: 'In-process',
            stage: 'active',
            title: 'Renewal Review in Progress',
            description: 'We are reviewing tenancy history, market benchmarks, and renewal options for your unit.',
            presentation: {
                tone: 'warning',
                icon: 'description', }
        },
        {
            id: 'renewal_terms_ready',
            serviceStatus: 'Renewal Terms Ready',
            stage: 'active',
            title: 'Renewal Terms Ready',
            description: 'Your proposed renewal terms are ready. Please review and confirm your preferred direction.',
            presentation: {
                tone: 'warning',
                icon: 'description',
                badge: 'REVIEW',
                badgeTone: 'warning',
                showActionRequired: true,
                actionKey: 'open_order_details'
            }
        },
        {
            id: 'request_completed',
            serviceStatus: 'Completed',
            stage: 'closed',
            title: 'Renewal Completed',
            description: 'Renewal and retention support is complete. The agreed plan is now finalized.',
            presentation: {
                tone: 'success',
                icon: 'check_circle', }
        },
        {
            id: 'request_canceled',
            serviceStatus: 'Canceled',
            stage: 'failed',
            title: 'Request Canceled',
            description: 'Your request was canceled. Please check details in your order history or contact support.',
            presentation: {
                tone: 'danger',
                icon: 'close_small',
            }
        },
    ],

    service_mortgage_or_property_liquidation: [
        {
            id: 'request_received',
            serviceStatus: 'Request Received',
            stage: 'todo',
            title: 'Request Received',
            description: 'Your supervision request has been received and assigned to the relevant team.',
            presentation: {
                tone: 'warning',
                icon: 'schedule', }
        },
        {
            id: 'in_process',
            serviceStatus: 'In-process',
            stage: 'active',
            title: 'Case Review in Progress',
            description: 'We are reviewing your mortgage/liquidation case scope, documents, and coordination requirements.',
            presentation: {
                tone: 'warning',
                icon: 'description', }
        },
        {
            id: 'strategy_ready',
            serviceStatus: 'Strategy Ready',
            stage: 'active',
            title: 'Strategy Ready',
            description: 'Your supervision strategy is ready. Please review and approve next execution steps.',
            presentation: {
                tone: 'warning',
                icon: 'description',
                badge: 'REVIEW',
                badgeTone: 'warning',
                showActionRequired: true,
                actionKey: 'open_order_details'
            }
        },
        {
            id: 'request_completed',
            serviceStatus: 'Completed',
            stage: 'closed',
            title: 'Supervision Completed',
            description: 'Mortgage/liquidation supervision has been completed successfully.',
            presentation: {
                tone: 'success',
                icon: 'check_circle', }
        },
        {
            id: 'request_canceled',
            serviceStatus: 'Canceled',
            stage: 'failed',
            title: 'Request Canceled',
            description: 'Your request was canceled. Please check details in your order history or contact support.',
            presentation: {
                tone: 'danger',
                icon: 'close_small',
            }
        },
    ],

    service_loan_against_rent: [
        {
            id: 'request_received',
            serviceStatus: 'Request Received',
            stage: 'todo',
            title: 'Request Received',
            description: 'Your loan assistance request has been received and queued for eligibility screening.',
            presentation: {
                tone: 'warning',
                icon: 'schedule', }
        },
        {
            id: 'in_process',
            serviceStatus: 'In-process',
            stage: 'active',
            title: 'Eligibility Review in Progress',
            description: 'We are reviewing your rent-backed loan readiness and required documentation checklist.',
            presentation: {
                tone: 'warning',
                icon: 'analytics', }
        },
        {
            id: 'offer_ready_for_review',
            serviceStatus: 'Offer Ready For Review',
            stage: 'active',
            title: 'Offer Ready for Review',
            description: 'A lender-ready proposal is prepared. Please review the terms and confirm how you want to proceed.',
            presentation: {
                tone: 'warning',
                icon: 'description',
                badge: 'REVIEW',
                badgeTone: 'warning',
                showActionRequired: true,
                actionKey: 'open_order_details'
            }
        },
        {
            id: 'request_completed',
            serviceStatus: 'Completed',
            stage: 'closed',
            title: 'Assistance Completed',
            description: 'Loan against rent assistance has been completed and your case handoff is finalized.',
            presentation: {
                tone: 'success',
                icon: 'check_circle', }
        },
        {
            id: 'request_canceled',
            serviceStatus: 'Canceled',
            stage: 'failed',
            title: 'Request Canceled',
            description: 'Your request was canceled. Please check details in your order history or contact support.',
            presentation: {
                tone: 'danger',
                icon: 'close_small',
            }
        },
    ],

    service_property_insurance: [
        {
            id: 'request_received',
            serviceStatus: 'Request Received',
            stage: 'todo',
            title: 'Request Received',
            description: 'Your insurance assistance request has been received and is awaiting policy assessment.',
            presentation: {
                tone: 'warning',
                icon: 'schedule', }
        },
        {
            id: 'in_process',
            serviceStatus: 'In-process',
            stage: 'active',
            title: 'Policy Assessment in Progress',
            description: 'We are reviewing your property profile and matching suitable insurance support options.',
            presentation: {
                tone: 'warning',
                icon: 'description', }
        },
        {
            id: 'quotes_ready_for_review',
            serviceStatus: 'Quotes Ready For Review',
            stage: 'active',
            title: 'Quotes Ready for Review',
            description: 'Insurance options are ready. Please review coverage and approve the preferred path.',
            presentation: {
                tone: 'warning',
                icon: 'description',
                badge: 'REVIEW',
                badgeTone: 'warning',
                showActionRequired: true,
                actionKey: 'open_order_details'
            }
        },
        {
            id: 'request_completed',
            serviceStatus: 'Completed',
            stage: 'closed',
            title: 'Insurance Assistance Completed',
            description: 'Your insurance support process is complete and policy guidance has been finalized.',
            presentation: {
                tone: 'success',
                icon: 'check_circle', }
        },
        {
            id: 'request_canceled',
            serviceStatus: 'Canceled',
            stage: 'failed',
            title: 'Request Canceled',
            description: 'Your request was canceled. Please check details in your order history or contact support.',
            presentation: {
                tone: 'danger',
                icon: 'close_small',
            }
        },
    ],

    service_finding_buyer: [
        {
            id: 'request_received',
            serviceStatus: 'Request Received',
            stage: 'todo',
            title: 'Request Received',
            description: 'Your finding-a-buyer request has been received and is now queued for sales coordination.',
            presentation: {
                tone: 'warning',
                icon: 'schedule', }
        },
        {
            id: 'in_process',
            serviceStatus: 'In-process',
            stage: 'active',
            title: 'Buyer Matching in Progress',
            description: 'We are actively matching your property with qualified buyer demand and handling follow-ups.',
            presentation: {
                tone: 'warning',
                icon: 'analytics', }
        },
        {
            id: 'request_completed',
            serviceStatus: 'Completed',
            stage: 'closed',
            title: 'Buyer Found Process Completed',
            description: 'Your finding-a-buyer request is completed. Check your order details for outcome and next steps.',
            presentation: {
                tone: 'success',
                icon: 'check_circle', }
        },
        {
            id: 'request_canceled',
            serviceStatus: 'Canceled',
            stage: 'failed',
            title: 'Request Canceled',
            description: 'Your request was canceled. Please check details in your order history or contact support.',
            presentation: {
                tone: 'danger',
                icon: 'close_small',
            }
        },
    ],

    service_property_sale_valuation: [
        {
            id: 'request_received',
            serviceStatus: 'Request Received',
            stage: 'todo',
            title: 'Request Received',
            description: 'Your sale valuation request has been received and queued for market assessment.',
            presentation: {
                tone: 'warning',
                icon: 'schedule', }
        },
        {
            id: 'in_process',
            serviceStatus: 'In-process',
            stage: 'active',
            title: 'Market Analysis in Progress',
            description: 'We are reviewing comparable transactions and current market indicators for your unit.',
            presentation: {
                tone: 'warning',
                icon: 'analytics', }
        },
        {
            id: 'valuation_draft_ready',
            serviceStatus: 'Valuation Draft Ready',
            stage: 'active',
            title: 'Valuation Draft Ready',
            description: 'Your draft sale valuation is ready. Please review the findings before final issuance.',
            presentation: {
                tone: 'warning',
                icon: 'description',
                badge: 'REVIEW',
                badgeTone: 'warning',
                showActionRequired: true,
                actionKey: 'open_order_details'
            }
        },
        {
            id: 'request_completed',
            serviceStatus: 'Completed',
            stage: 'closed',
            title: 'Sale Valuation Completed',
            description: 'Your property sale valuation is complete and ready for your next listing decision.',
            presentation: {
                tone: 'success',
                icon: 'download',
                iconPosition: 'end',
                actionKey: 'open_download',
                colorOverride: 'var(--info)'
            }
        },
        {
            id: 'request_canceled',
            serviceStatus: 'Canceled',
            stage: 'failed',
            title: 'Request Canceled',
            description: 'Your request was canceled. Please check details in your order history or contact support.',
            presentation: {
                tone: 'danger',
                icon: 'close_small',
            }
        },
    ],

    service_preventive_maintenance_check: [
        {
            id: 'request_received',
            serviceStatus: 'Request Received',
            stage: 'todo',
            title: 'Request Received',
            description: 'Your preventive maintenance request has been received and is pending scheduling.',
            presentation: {
                tone: 'warning',
                icon: 'schedule', }
        },
        {
            id: 'in_process',
            serviceStatus: 'In-process',
            stage: 'active',
            title: 'Inspection in Progress',
            description: 'Our team is conducting the preventive review across key systems in your property.',
            presentation: {
                tone: 'warning',
                icon: 'construction', }
        },
        {
            id: 'checklist_ready_for_review',
            serviceStatus: 'Checklist Ready For Review',
            stage: 'active',
            title: 'Checklist Ready for Review',
            description: 'The preventive checklist is ready. Please review findings and suggested actions.',
            presentation: {
                tone: 'warning',
                icon: 'description',
                badge: 'REVIEW',
                badgeTone: 'warning',
                showActionRequired: true,
                actionKey: 'open_order_details'
            }
        },
        {
            id: 'request_completed',
            serviceStatus: 'Completed',
            stage: 'closed',
            title: 'Preventive Check Completed',
            description: 'Preventive maintenance check is complete and recommendations are finalized.',
            presentation: {
                tone: 'success',
                icon: 'check_circle', }
        },
        {
            id: 'request_canceled',
            serviceStatus: 'Canceled',
            stage: 'failed',
            title: 'Request Canceled',
            description: 'Your request was canceled. Please check details in your order history or contact support.',
            presentation: {
                tone: 'danger',
                icon: 'close_small',
            }
        },
    ],

    service_routine_property_inspection: [
        {
            id: 'request_received',
            serviceStatus: 'Request Received',
            stage: 'todo',
            title: 'Request Received',
            description: 'Your routine inspection request has been received and moved to planning.',
            presentation: {
                tone: 'warning',
                icon: 'schedule', }
        },
        {
            id: 'in_process',
            serviceStatus: 'In-process',
            stage: 'active',
            title: 'Inspection in Progress',
            description: 'A routine inspection is being conducted to capture current property condition and tenancy observations.',
            presentation: {
                tone: 'warning',
                icon: 'fact_check', }
        },
        {
            id: 'inspection_report_ready',
            serviceStatus: 'Inspection Report Ready',
            stage: 'active',
            title: 'Inspection Report Ready',
            description: 'Your routine inspection report is ready. Please review notes and recommended follow-up actions.',
            presentation: {
                tone: 'warning',
                icon: 'description',
                badge: 'REVIEW',
                badgeTone: 'warning',
                showActionRequired: true,
                actionKey: 'open_order_details'
            }
        },
        {
            id: 'request_completed',
            serviceStatus: 'Completed',
            stage: 'closed',
            title: 'Routine Inspection Completed',
            description: 'Routine property inspection is completed and all required updates have been shared.',
            presentation: {
                tone: 'success',
                icon: 'check_circle', }
        },
        {
            id: 'request_canceled',
            serviceStatus: 'Canceled',
            stage: 'failed',
            title: 'Request Canceled',
            description: 'Your request was canceled. Please check details in your order history or contact support.',
            presentation: {
                tone: 'danger',
                icon: 'close_small',
            }
        },
    ],
    service_pre_move_out_maintenance_inspection: [
        {
            id: 'request_received',
            serviceStatus: 'Request Received',
            stage: 'todo',
            title: 'Request Received',
            description: 'Your pre-move-out maintenance inspection request has been received and queued for coordination.',
            presentation: {
                tone: 'warning',
                icon: 'schedule', }
        },
        {
            id: 'in_process',
            serviceStatus: 'In-process',
            stage: 'active',
            title: 'Inspection in Progress',
            description: 'We are assessing the unit condition and flagging maintenance items before move-out handover.',
            presentation: {
                tone: 'warning',
                icon: 'fact_check', }
        },
        {
            id: 'request_completed',
            serviceStatus: 'Completed',
            stage: 'closed',
            title: 'Inspection Completed',
            description: 'Pre-move-out maintenance inspection is completed and findings are ready for next action.',
            presentation: {
                tone: 'success',
                icon: 'check_circle', }
        },
    ],
    service_snagging_property: [
        {
            id: 'request_received',
            serviceStatus: 'Request Received',
            stage: 'todo',
            title: 'Request Received',
            description: 'Your snagging request has been received and moved to planning.',
            presentation: {
                tone: 'warning',
                icon: 'schedule', }
        },
        {
            id: 'in_process',
            serviceStatus: 'In-process',
            stage: 'active',
            title: 'Snagging in Progress',
            description: 'Our team is reviewing your property to capture snags and required rectification points.',
            presentation: {
                tone: 'warning',
                icon: 'fact_check', }
        },
        {
            id: 'request_completed',
            serviceStatus: 'Completed',
            stage: 'closed',
            title: 'Snagging Completed',
            description: 'Snagging support is completed and the findings have been finalized.',
            presentation: {
                tone: 'success',
                icon: 'check_circle', }
        },
    ],
    service_consultation_request: [
        {
            id: 'request_received',
            serviceStatus: 'Request Received',
            stage: 'todo',
            title: 'Request Received',
            description: 'Your consultation request has been received.',
            presentation: {
                tone: 'warning',
                icon: 'schedule', }
        },
        {
            id: 'in_process',
            serviceStatus: 'In-process',
            stage: 'active',
            title: 'Consultation in Progress',
            description: 'Our team is reviewing your request and preparing the right support path.',
            presentation: {
                tone: 'warning',
                icon: 'support_agent', }
        },
        {
            id: 'request_completed',
            serviceStatus: 'Completed',
            stage: 'closed',
            title: 'Consultation Completed',
            description: 'Your consultation request has been completed.',
            presentation: {
                tone: 'success',
                icon: 'check_circle', }
        },
    ],
    service_tmc: [
        {
            id: 'request_received',
            serviceStatus: 'Request Received',
            stage: 'todo',
            title: 'Request Received',
            description: 'Your tenant management coordination request has been received.',
            presentation: {
                tone: 'warning',
                icon: 'schedule', }
        },
        {
            id: 'in_process',
            serviceStatus: 'In-process',
            stage: 'active',
            title: 'Coordination in Progress',
            description: 'We are reviewing your request and coordinating the next tenant management actions.',
            presentation: {
                tone: 'warning',
                icon: 'support_agent', }
        },
        {
            id: 'request_completed',
            serviceStatus: 'Completed',
            stage: 'closed',
            title: 'Request Completed',
            description: 'Tenant management coordination request has been completed.',
            presentation: {
                tone: 'success',
                icon: 'check_circle', }
        },
    ],
    service_bso_unit_viewing_request: [
        {
            id: 'request_received',
            serviceStatus: 'Request Received',
            stage: 'todo',
            title: 'Request Received',
            description: 'Your BSO catalogue viewing request has been received.',
            presentation: {
                tone: 'warning',
                icon: 'schedule', }
        },
        {
            id: 'in_process',
            serviceStatus: 'In-process',
            stage: 'active',
            title: 'Viewing Coordination in Progress',
            description: 'We are coordinating your requested viewing details with the relevant team.',
            presentation: {
                tone: 'warning',
                icon: 'schedule', }
        },
        {
            id: 'request_completed',
            serviceStatus: 'Completed',
            stage: 'closed',
            title: 'Viewing Request Completed',
            description: 'Your viewing request has been completed.',
            presentation: {
                tone: 'success',
                icon: 'check_circle', }
        },
    ],
    service_bso_unit_offer_request: [
        {
            id: 'request_received',
            serviceStatus: 'Request Received',
            stage: 'todo',
            title: 'Request Received',
            description: 'Your BSO catalogue offer request has been received.',
            presentation: {
                tone: 'warning',
                icon: 'schedule', }
        },
        {
            id: 'in_process',
            serviceStatus: 'In-process',
            stage: 'active',
            title: 'Offer Processing in Progress',
            description: 'We are processing your submitted offer request.',
            presentation: {
                tone: 'warning',
                icon: 'description', }
        },
        {
            id: 'request_completed',
            serviceStatus: 'Completed',
            stage: 'closed',
            title: 'Offer Request Completed',
            description: 'Your offer request has been completed.',
            presentation: {
                tone: 'success',
                icon: 'check_circle', }
        },
    ],
    service_finding_property_rent: [
        {
            id: 'request_received',
            serviceStatus: 'Request Received',
            stage: 'todo',
            title: 'Request Received',
            description: 'Your property rental search request has been received.',
            presentation: {
                tone: 'warning',
                icon: 'schedule', }
        },
        {
            id: 'in_process',
            serviceStatus: 'In-process',
            stage: 'active',
            title: 'Search in Progress',
            description: 'Our team is reviewing your requirements and coordinating the property search.',
            presentation: {
                tone: 'warning',
                icon: 'support_agent', }
        },
        {
            id: 'request_completed',
            serviceStatus: 'Completed',
            stage: 'closed',
            title: 'Request Completed',
            description: 'Your property rental search request has been completed.',
            presentation: {
                tone: 'success',
                icon: 'check_circle', }
        },
        {
            id: 'request_canceled',
            serviceStatus: 'Canceled',
            stage: 'failed',
            title: 'Request Canceled',
            description: 'Your property rental search request has been canceled.',
            presentation: {
                tone: 'danger',
                icon: 'description', }
        },
    ],
    service_finding_property_sale: [
        {
            id: 'request_received',
            serviceStatus: 'Request Received',
            stage: 'todo',
            title: 'Request Received',
            description: 'Your property purchase search request has been received.',
            presentation: {
                tone: 'warning',
                icon: 'schedule', }
        },
        {
            id: 'in_process',
            serviceStatus: 'In-process',
            stage: 'active',
            title: 'Search in Progress',
            description: 'Our team is reviewing your requirements and coordinating the property search.',
            presentation: {
                tone: 'warning',
                icon: 'support_agent', }
        },
        {
            id: 'request_completed',
            serviceStatus: 'Completed',
            stage: 'closed',
            title: 'Request Completed',
            description: 'Your property purchase search request has been completed.',
            presentation: {
                tone: 'success',
                icon: 'check_circle', }
        },
        {
            id: 'request_canceled',
            serviceStatus: 'Canceled',
            stage: 'failed',
            title: 'Request Canceled',
            description: 'Your property purchase search request has been canceled.',
            presentation: {
                tone: 'danger',
                icon: 'description', }
        },
    ],
    contract_renewal_drafting: [
        {
            id: 'request_received',
            serviceStatus: 'Request Received',
            stage: 'todo',
            title: 'Request Received',
            description: 'Your rental contract renewal progression request has been received.',
            presentation: {
                tone: 'warning',
                icon: 'schedule', }
        },
        {
            id: 'in_process',
            serviceStatus: 'In-process',
            stage: 'active',
            title: 'Contract Renewal in Progress',
            description: 'We are coordinating your rental contract renewal documentation.',
            presentation: {
                tone: 'warning',
                icon: 'description', }
        },
        {
            id: 'request_completed',
            serviceStatus: 'Completed',
            stage: 'closed',
            title: 'Contract Renewal Completed',
            description: 'Rental contract renewal progression has been completed.',
            presentation: {
                tone: 'success',
                icon: 'check_circle', }
        },
        {
            id: 'request_canceled',
            serviceStatus: 'Canceled',
            stage: 'failed',
            title: 'Request Canceled',
            description: 'Rental contract renewal progression has been canceled.',
            presentation: {
                tone: 'danger',
                icon: 'description', }
        },
    ],
    sale_contract_progression: [
        {
            id: 'request_received',
            serviceStatus: 'Request Received',
            stage: 'todo',
            title: 'Request Received',
            description: 'Your sale contract progression request has been received.',
            presentation: {
                tone: 'warning',
                icon: 'schedule', }
        },
        {
            id: 'in_process',
            serviceStatus: 'In-process',
            stage: 'active',
            title: 'Sale Contract in Progress',
            description: 'We are coordinating your sale contract documentation.',
            presentation: {
                tone: 'warning',
                icon: 'description', }
        },
        {
            id: 'request_completed',
            serviceStatus: 'Completed',
            stage: 'closed',
            title: 'Sale Contract Completed',
            description: 'Sale contract progression has been completed.',
            presentation: {
                tone: 'success',
                icon: 'check_circle', }
        },
        {
            id: 'request_canceled',
            serviceStatus: 'Canceled',
            stage: 'failed',
            title: 'Request Canceled',
            description: 'Sale contract progression has been canceled.',
            presentation: {
                tone: 'danger',
                icon: 'description', }
        },
    ],
    service_power_of_attorney: [
        {
            id: 'request_received',
            serviceStatus: 'Request Received',
            stage: 'todo',
            title: 'Request Received',
            description: 'Your power of attorney request has been received.',
            presentation: {
                tone: 'warning',
                icon: 'schedule', }
        },
        {
            id: 'in_process',
            serviceStatus: 'In-process',
            stage: 'active',
            title: 'Power of Attorney in Progress',
            description: 'We are reviewing your request and coordinating the next steps.',
            presentation: {
                tone: 'warning',
                icon: 'description', }
        },
        {
            id: 'request_completed',
            serviceStatus: 'Completed',
            stage: 'closed',
            title: 'Request Completed',
            description: 'Your power of attorney request has been completed.',
            presentation: {
                tone: 'success',
                icon: 'check_circle', }
        },
        {
            id: 'request_canceled',
            serviceStatus: 'Canceled',
            stage: 'failed',
            title: 'Request Canceled',
            description: 'Your power of attorney request has been canceled.',
            presentation: {
                tone: 'danger',
                icon: 'description', }
        },
    ],
    service_will_registration_assistance: [
        {
            id: 'request_received',
            serviceStatus: 'Request Received',
            stage: 'todo',
            title: 'Request Received',
            description: 'Your will writing and registration assistance request has been received.',
            presentation: {
                tone: 'warning',
                icon: 'schedule', }
        },
        {
            id: 'in_process',
            serviceStatus: 'In-process',
            stage: 'active',
            title: 'Assistance in Progress',
            description: 'We are reviewing your request and coordinating the next steps.',
            presentation: {
                tone: 'warning',
                icon: 'description', }
        },
        {
            id: 'request_completed',
            serviceStatus: 'Completed',
            stage: 'closed',
            title: 'Request Completed',
            description: 'Your will writing and registration assistance request has been completed.',
            presentation: {
                tone: 'success',
                icon: 'check_circle', }
        },
        {
            id: 'request_canceled',
            serviceStatus: 'Canceled',
            stage: 'failed',
            title: 'Request Canceled',
            description: 'Your will writing and registration assistance request has been canceled.',
            presentation: {
                tone: 'danger',
                icon: 'description', }
        },
    ],
    service_legal_notice: [
        {
            id: 'request_received',
            serviceStatus: 'Request Received',
            stage: 'todo',
            title: 'Request Received',
            description: 'Your legal notice request has been received.',
            presentation: {
                tone: 'warning',
                icon: 'schedule', }
        },
        {
            id: 'in_process',
            serviceStatus: 'In-process',
            stage: 'active',
            title: 'Legal Notice in Progress',
            description: 'We are reviewing your request and coordinating the next steps.',
            presentation: {
                tone: 'warning',
                icon: 'description', }
        },
        {
            id: 'request_completed',
            serviceStatus: 'Completed',
            stage: 'closed',
            title: 'Request Completed',
            description: 'Your legal notice request has been completed.',
            presentation: {
                tone: 'success',
                icon: 'check_circle', }
        },
        {
            id: 'request_canceled',
            serviceStatus: 'Canceled',
            stage: 'failed',
            title: 'Request Canceled',
            description: 'Your legal notice request has been canceled.',
            presentation: {
                tone: 'danger',
                icon: 'description', }
        },
    ],
} as const satisfies Record<string, ServicePipeline>

export const CANONICAL_ACTIVE_SERVICE_PIPELINES = {
    find_tenant: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.find_tenant),
    unit_photography: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.unit_photography),
    request_inspection: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.request_inspection),
    property_rent_valuation: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.property_rent_valuation),
    noc_generation: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.noc_generation),
    tenant_screening: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.tenant_screening),
    // Transitional: dedicated pipelines for these active FT add-ons are not finalized yet.
    // Keep them canonical in the registry to avoid legacy ids being first-class.
    contract_drafting: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.find_tenant),
    inventory_checklist: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.inventory_checklist),
    lease_execution: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.lease_execution),
    utility_setup: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.find_tenant),
    service_property_management: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.service_property_management),
    service_unit_maintenance: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.service_unit_maintenance),
    service_gifting_property: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.service_gifting_property),
    service_rental_dispute: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.service_rental_dispute),
    service_tenant_renewal_retention: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.service_tenant_renewal_retention),
    service_mortgage_or_property_liquidation: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.service_mortgage_or_property_liquidation),
    service_loan_against_rent: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.service_loan_against_rent),
    service_property_insurance: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.service_property_insurance),
    service_finding_buyer: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.service_finding_buyer),
    service_property_value_report: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.property_rent_valuation),
    service_property_sale_valuation: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.service_property_sale_valuation),
    service_preventive_maintenance_check: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.service_preventive_maintenance_check),
    service_routine_property_inspection: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.service_routine_property_inspection),
    service_pre_move_out_maintenance_inspection: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.service_pre_move_out_maintenance_inspection),
    service_snagging_property: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.service_snagging_property),
    service_consultation_request: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.service_consultation_request),
    service_tmc: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.service_tmc),
    service_bso_unit_viewing_request: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.service_bso_unit_viewing_request),
    service_bso_unit_offer_request: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.service_bso_unit_offer_request),
    service_finding_property_rent: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.service_finding_property_rent),
    service_finding_property_sale: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.service_finding_property_sale),
    contract_renewal_drafting: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.contract_renewal_drafting),
    sale_contract_progression: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.sale_contract_progression),
    service_power_of_attorney: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.service_power_of_attorney),
    service_will_registration_assistance: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.service_will_registration_assistance),
    service_legal_notice: withPipelineLifecycleMetadata(RAW_SERVICE_PIPELINES.service_legal_notice),
} as const satisfies Record<string, ServicePipeline>

export const SERVICE_PIPELINES = CANONICAL_ACTIVE_SERVICE_PIPELINES

export type ServicePipelineKey = keyof typeof SERVICE_PIPELINES
export type ServicePipelineStageByKey<TPipelineKey extends ServicePipelineKey> =
    (typeof SERVICE_PIPELINES)[TPipelineKey][number]
export type ServiceStatusByPipelineKey<TPipelineKey extends ServicePipelineKey> =
    ServicePipelineStageByKey<TPipelineKey>['serviceStatus']

export const getServicePipeline = (serviceId: string): readonly ServicePipelineStage[] => {
    const configuredPipeline = liveServicePipelines?.[serviceId]
    if (configuredPipeline != null) return configuredPipeline

    // Finding Tenant and its NOC trigger are shipped together and must remain
    // operable when an older config record does not yet expose ServicePipelines.
    if (serviceId === 'find_tenant' || serviceId === 'noc_generation') {
        return SERVICE_PIPELINES[serviceId]
    }

    return []
}

export const getPipelineStatuses = (serviceId: string): string[] => {
    return Array.from(new Set(getServicePipeline(serviceId).map((stage) => stage.serviceStatus)))
}

export const getPipelineTerminalStatuses = (serviceId: string): string[] => {
    return Array.from(new Set(getServicePipeline(serviceId).filter(isTerminalPipelineStage).map((stage) => stage.serviceStatus)))
}

